import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/apiClient';

interface UsePermissionsReturn {
  permissions: string[];
  loading: boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  isAdmin: boolean;
}

export const usePermissions = (): UsePermissionsReturn => {
  const { user, isAuthenticated } = useAuth();
  const [permissions, setPermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPermissions = async () => {
      if (!isAuthenticated || !user) {
        setPermissions([]);
        setLoading(false);
        return;
      }

      // Admin users have all permissions
      if (user.role === 'admin') {
        setPermissions(['*']); // Wildcard for admin
        setLoading(false);
        return;
      }

      try {
        // Get user permissions from backend
        const response = await apiClient.get('/user/permissions');
        setPermissions(response.permissions || []);
      } catch (error) {
        setPermissions([]);
      } finally {
        setLoading(false);
      }
    };

    loadPermissions();
  }, [isAuthenticated, user]);

  const hasPermission = (permission: string): boolean => {
    if (!isAuthenticated || !user) return false;

    // Admin users have all permissions
    if (user.role === 'admin') return true;

    return permissions.includes(permission);
  };

  const hasAnyPermission = (permissionList: string[]): boolean => {
    if (!isAuthenticated || !user) return false;

    // Admin users have all permissions
    if (user.role === 'admin') return true;

    const hasAny = permissionList.some(permission => permissions.includes(permission));

    return hasAny;
  };

  const isAdmin = user?.role === 'admin';

  return {
    permissions,
    loading,
    hasPermission,
    hasAnyPermission,
    isAdmin,
  };
};
